import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useLinkedFringesQuery } from 'utils/api/queries';
import { useLinkedFringesMutation } from 'utils/api/mutations';
import {
  setCheckedRows as setCheckedRowsAction,
  setShowModal as setShowModalAction,
  setSelectedFringe as setSelectedFringeAction,
  setSelectedLinkGroup as setSelectedLinkGroupAction,
  setFringeGroups as setFringeGroupsAction,
  setIsUnlinkAllChecked as setIsUnlinkAllCheckedAction,
  setIsLinkAllChecked as setIsLinkAllCheckedAction,
  setHasLinkedRows as setHasLinkedRowsAction,
} from 'store/slices/detailedViewSlice';

import {
  normalizeLinkGroupsFromAPI,
  applyRangeCheckboxChange,
  applyRangeSelectAll,
} from './fringeLinkingUtils';

let _nextTempId = 0;
function makeTempRangeId() {
  _nextTempId += 1;
  return `__new_${_nextTempId}_${Date.now()}`;
}

/**
 * Custom hook for managing fringe linking functionality
 */
export const useFringeLinking = ({
  selectedBudget,
  selectedItem,
  level,
  detailsRows,
  refetch,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Redux state
  const checkedRows = useSelector(state => state.detailedView.checkedRows);
  const showModal = useSelector(state => state.detailedView.showModal);
  const selectedFringe = useSelector(
    state => state.detailedView.selectedFringe
  );
  const fringeGroups = useSelector(state => state.detailedView.fringeGroups);
  const selectedLinkGroup = useSelector(
    state => state.detailedView.selectedLinkGroup
  );
  const isUnlinkAllChecked = useSelector(
    state => state.detailedView.isUnlinkAllChecked
  );
  const isLinkAllChecked = useSelector(
    state => state.detailedView.isLinkAllChecked
  );
  const hasLinkedRows = useSelector(state => state.detailedView.hasLinkedRows);

  // Local state
  const [isLoading, setIsLoading] = useState(false);

  // Multi-column range state (local to modal lifecycle)
  const [rangeColumns, setRangeColumns] = useState([]);
  const [rangeCheckedRows, setRangeCheckedRows] = useState({});

  // API queries and mutations
  const { mutate: linkFringes } =
    useLinkedFringesMutation.useLinkFringesMutation();
  const { data: linkedFringesData, refetch: refetchLinkedFringes } =
    useLinkedFringesQuery.useLinkedFringesByGroupQuery({
      enabled: true,
      filters: {
        budget_id: selectedBudget?.budget,
      },
    });

  // Computed values
  const fringeRowCount = useMemo(() => {
    const countMap = {};
    detailsRows[selectedItem?.id]?.forEach(row => {
      if (row.fringe && Array.isArray(row.fringe)) {
        row.fringe?.forEach(fringe => {
          const fringeLabel = fringe.label;
          countMap[fringeLabel] = (countMap[fringeLabel] || 0) + 1;
        });
      }
    });
    return countMap;
  }, [detailsRows, selectedItem]);

  const fringeRows = useMemo(() => {
    const rowsMap = {};
    detailsRows[selectedItem?.id]?.forEach(row => {
      if (row.fringe && Array.isArray(row.fringe)) {
        row.fringe?.forEach(fringe => {
          const fringeLabel = fringe.label;
          if (!rowsMap[fringeLabel]) {
            rowsMap[fringeLabel] = [];
          }
          rowsMap[fringeLabel].push(row.id.toString());
        });
      }
    });
    return rowsMap;
  }, [detailsRows, selectedItem]);

  const filteredRows = useMemo(() => {
    if (!selectedFringe || !fringeRows[selectedFringe?.label]) return [];
    return (
      detailsRows[selectedItem?.id]?.filter(row =>
        row.id
          ? fringeRows[selectedFringe?.label]?.includes(row.id.toString())
          : false
      ) || []
    );
  }, [detailsRows, selectedItem, selectedFringe, fringeRows]);

  const filteredFringe = useMemo(() => {
    return selectedFringe?.display_name || null;
  }, [selectedFringe]);

  const linkGroupsForFringe = useMemo(() => {
    return selectedFringe?.id ? fringeGroups[selectedFringe.id] || {} : {};
  }, [selectedFringe?.id, fringeGroups]);

  // Helper functions
  const formatFringeGroups = normalizeLinkGroupsFromAPI;

  // Event handlers (legacy single-column, kept for chip checkbox in table rows)
  const handleCheckboxChange = (fringeId, rowId) => event => {
    const isRowChecked = event.target.checked;
    const updated = { ...checkedRows };
    updated[fringeId] = {
      ...(updated[fringeId] || {}),
      [rowId]: isRowChecked,
    };
    if (isUnlinkAllChecked && isRowChecked) {
      dispatch(setIsUnlinkAllCheckedAction(false));
    }
    if (isLinkAllChecked && !isRowChecked) {
      dispatch(setIsLinkAllCheckedAction(false));
    }
    dispatch(setCheckedRowsAction(updated));
  };

  const handleOpenModal = fringe => {
    dispatch(setSelectedFringeAction(fringe));
    dispatch(setSelectedLinkGroupAction(null));
    dispatch(setShowModalAction(true));
  };

  const handleModalClose = () => {
    dispatch(setShowModalAction(false));
    dispatch(setSelectedFringeAction(null));
    dispatch(setSelectedLinkGroupAction(null));
    dispatch(setCheckedRowsAction({}));
    setRangeColumns([]);
    setRangeCheckedRows({});
  };

  // --- Multi-column range handlers ---

  const handleRangeCheckboxChange = useCallback((rangeId, rowId, isChecked) => {
    setRangeCheckedRows(prev =>
      applyRangeCheckboxChange(prev, rangeId, rowId, isChecked)
    );
  }, []);

  const handleRangeSelectAll = useCallback(
    (rangeId, isChecked) => {
      setRangeCheckedRows(prev =>
        applyRangeSelectAll(prev, rangeId, isChecked, filteredRows)
      );
    },
    [filteredRows]
  );

  const handleAddRange = useCallback(() => {
    const newId = makeTempRangeId();
    setRangeColumns(prev => [...prev, { id: newId }]);
    setRangeCheckedRows(prev => ({ ...prev, [newId]: {} }));
  }, []);

  const handleRemoveRange = useCallback(rangeId => {
    setRangeColumns(prev => prev.filter(col => col.id !== rangeId));
    setRangeCheckedRows(prev => {
      const next = { ...prev };
      delete next[rangeId];
      return next;
    });
  }, []);

  // --- Legacy single-column handler (kept for backward compat but no longer used by modal) ---
  const handleModalCheckboxChange = (fringeId, rowId, isChecked) => {
    if (fringeId === 'ALL') {
      const updated = { ...checkedRows };
      const fringeChecked = { ...(updated[selectedFringe?.id] || {}) };
      filteredRows.forEach(row => {
        fringeChecked[row.id.toString()] = isChecked;
      });
      updated[selectedFringe?.id] = fringeChecked;
      dispatch(setCheckedRowsAction(updated));
      if (isChecked) {
        dispatch(setIsUnlinkAllCheckedAction(false));
      }
      return;
    }

    const updated = { ...checkedRows };
    updated[fringeId] = {
      ...(updated[fringeId] || {}),
      [rowId]: isChecked,
    };
    if (isUnlinkAllChecked && isChecked) {
      dispatch(setIsUnlinkAllCheckedAction(false));
    }
    if (isLinkAllChecked && !isChecked) {
      dispatch(setIsLinkAllCheckedAction(false));
    }

    const fringeChecked = updated[fringeId] || {};
    const allChecked = filteredRows.every(
      row => fringeChecked[row.id.toString()] === true
    );
    if (allChecked && filteredRows.length > 0) {
      dispatch(setIsLinkAllCheckedAction(true));
    }

    dispatch(setCheckedRowsAction(updated));
  };

  // --- Save: build grouped payload from rangeCheckedRows ---
  const handleLinkFringes = async () => {
    const detailSheetType =
      level === 0
        ? 'swimlane'
        : level === 1
        ? 'line-item'
        : 'line-item-children';

    // Build grouped payload from all range columns
    const groupPayload = [];
    const allIncludedIds = [];

    rangeColumns.forEach(col => {
      const checks = rangeCheckedRows[col.id] || {};
      const ids = Object.entries(checks)
        .filter(([, v]) => v === true)
        .map(([rowId]) => parseInt(rowId, 10));

      if (ids.length === 0) return;
      allIncludedIds.push(...ids);

      // For existing groups keep their original id; for new temp columns generate a UUID
      let groupId = col.id;
      if (groupId.startsWith('__new_')) {
        groupId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/x/g, () =>
                Math.floor(Math.random() * 16).toString(16)
              );
      }
      groupPayload.push({ [groupId]: ids });
    });

    // Backward compat: single range -> flat list
    const useFlatPayload =
      groupPayload.length === 1 && Object.values(groupPayload[0])[0].length > 0;

    const payload = {
      detail_sheet_ids: useFlatPayload
        ? Object.values(groupPayload[0])[0]
        : groupPayload,
      fringe_id: selectedFringe.id,
      detail_sheet_type: detailSheetType,
      swimlane_id: selectedItem?.id,
    };

    if (useFlatPayload) {
      const allFilteredIds = filteredRows.map(row => row.id);
      const excludedIds = allFilteredIds.filter(
        id => !allIncludedIds.includes(id)
      );
      payload.detail_sheet_ids_exclude = excludedIds;
    }

    setIsLoading(true);
    linkFringes(payload, {
      onSuccess: async () => {
        const invalidatePromises = [
          queryClient.invalidateQueries([
            'get-swimlanes-details',
            {
              id: selectedItem?.id,
              type: level === 0 ? 'swimlane' : 'line-item',
            },
          ]),
          queryClient.invalidateQueries(['get-swimlanes-in-budget']),
          refreshLinkedFringes(),
          refetch(),
        ];

        if (selectedBudget?.budget) {
          invalidatePromises.push(
            queryClient.invalidateQueries([
              'get-contractual-charges',
              selectedBudget.budget,
            ]),
            queryClient.invalidateQueries([
              'get-film-incentives',
              selectedBudget.budget,
            ])
          );
        }

        await Promise.all(invalidatePromises);

        handleModalClose();
        setIsLoading(false);
      },
      onError: () => {
        toast.error('Failed to link fringes.');
        setIsLoading(false);
      },
    });
  };

  const refreshLinkedFringes = () => {
    refetchLinkedFringes().then(({ data: freshData }) => {
      const updatedFringeGroups = formatFringeGroups(freshData?.link_groups);
      dispatch(setFringeGroupsAction(updatedFringeGroups));
    });
  };

  // Effects
  useEffect(() => {
    if (linkedFringesData?.data?.link_groups) {
      const initialFringeGroups = formatFringeGroups(
        linkedFringesData.data.link_groups
      );
      dispatch(setFringeGroupsAction(initialFringeGroups));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedFringesData]);

  // Initialize multi-column state when modal opens
  useEffect(() => {
    if (showModal && selectedFringe) {
      const groupsForFringe = fringeGroups[selectedFringe?.id] || {};
      const existingGroupIds = Object.keys(groupsForFringe);

      // Build columns from existing groups; add one empty column if none exist
      const cols = existingGroupIds.map(id => ({ id }));
      if (cols.length === 0) {
        cols.push({ id: makeTempRangeId() });
      }

      // Build checked rows from existing data
      const checked = {};
      cols.forEach(col => {
        const rowIds = groupsForFringe[col.id] || [];
        const rowChecks = {};
        rowIds.forEach(rid => {
          rowChecks[rid] = true;
        });
        checked[col.id] = rowChecks;
      });

      setRangeColumns(cols);
      setRangeCheckedRows(checked);

      dispatch(setHasLinkedRowsAction(existingGroupIds.length > 0));
      dispatch(setIsUnlinkAllCheckedAction(false));
      dispatch(setIsLinkAllCheckedAction(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, selectedFringe, fringeGroups]);

  return {
    // State
    checkedRows,
    showModal,
    selectedFringe,
    selectedLinkGroup,
    fringeGroups,
    isUnlinkAllChecked,
    isLinkAllChecked,
    hasLinkedRows,
    isLoading,
    fringeRowCount,
    fringeRows,
    filteredRows,
    filteredFringe,
    linkGroupsForFringe,

    // Multi-column range state
    rangeColumns,
    rangeCheckedRows,

    // Actions
    handleCheckboxChange,
    handleOpenModal,
    handleModalClose,
    handleModalCheckboxChange,
    handleLinkFringes,
    refreshLinkedFringes,

    // Multi-column range actions
    handleRangeCheckboxChange,
    handleRangeSelectAll,
    handleAddRange,
    handleRemoveRange,

    // Redux actions for external use
    setSelectedFringe: fringe => dispatch(setSelectedFringeAction(fringe)),
    setSelectedLinkGroup: linkGroup =>
      dispatch(setSelectedLinkGroupAction(linkGroup)),
    setFringeGroups: groups => dispatch(setFringeGroupsAction(groups)),
    setIsLinkAllChecked: value => dispatch(setIsLinkAllCheckedAction(value)),
    setIsUnlinkAllChecked: value =>
      dispatch(setIsUnlinkAllCheckedAction(value)),
  };
};
